import { lerpYaw, type Direction } from '@etown/shared';

export interface Snapshot {
  /** Empfangszeitpunkt in ms (performance.now()-Basis). */
  t: number;
  x: number;
  y: number;
  direction: Direction;
  /** Nur von Nutzern der Ego-Ansicht gesetzt. */
  yaw?: number;
}

export interface SampledPose {
  x: number;
  y: number;
  direction: Direction;
  yaw?: number;
  /** true, wenn zwischen zwei Snapshots interpoliert wird (Avatar "laeuft"). */
  moving: boolean;
}

const MAX_SNAPSHOTS = 30;

/**
 * Snapshot-Puffer fuer Remote-Avatare: Positionen werden bewusst leicht
 * in der Vergangenheit gerendert (INTERPOLATION_DELAY_MS) und zwischen den
 * letzten beiden Server-Ticks linear interpoliert — fluessige Bewegung
 * trotz 15-Hz-Updates. Keine Extrapolation: bei Paketverlust friert der
 * Avatar an der letzten Position ein (besser als Durch-Waende-Gleiten).
 */
export class SnapshotBuffer {
  private readonly snapshots: Snapshot[] = [];

  push(snapshot: Snapshot): void {
    // Nicht-monotone Zeitstempel verwerfen (Reorder kommt bei TCP nicht vor,
    // aber doppelte Ticks koennen identische Zeiten tragen)
    const last = this.snapshots[this.snapshots.length - 1];
    if (last && snapshot.t <= last.t) return;
    this.snapshots.push(snapshot);
    if (this.snapshots.length > MAX_SNAPSHOTS) this.snapshots.shift();
  }

  /** Pose zum Render-Zeitpunkt t (bereits um das Delay reduziert). */
  sample(t: number): SampledPose | null {
    const snaps = this.snapshots;
    if (snaps.length === 0) return null;

    const first = snaps[0]!;
    if (t <= first.t || snaps.length === 1) {
      return { x: first.x, y: first.y, direction: first.direction, yaw: first.yaw, moving: false };
    }

    for (let i = snaps.length - 1; i >= 0; i -= 1) {
      const a = snaps[i]!;
      if (a.t <= t) {
        const b = snaps[i + 1];
        if (!b) {
          // Hinter dem neuesten Snapshot: einfrieren statt extrapolieren
          return { x: a.x, y: a.y, direction: a.direction, yaw: a.yaw, moving: false };
        }
        const alpha = (t - a.t) / (b.t - a.t);
        return {
          x: a.x + (b.x - a.x) * alpha,
          y: a.y + (b.y - a.y) * alpha,
          direction: b.direction,
          // Ueber den kuerzeren Bogen drehen, sonst rotiert der Avatar am
          // Nulldurchgang einmal komplett
          yaw: a.yaw !== undefined && b.yaw !== undefined ? lerpYaw(a.yaw, b.yaw, alpha) : b.yaw,
          moving: Math.abs(b.x - a.x) + Math.abs(b.y - a.y) > 0.001,
        };
      }
    }
    return { x: first.x, y: first.y, direction: first.direction, yaw: first.yaw, moving: false };
  }
}
